import { Visibility } from '@fullstory/proto/fs/gcloud/dstore/protoauditstore';
import { SetMetricRequest, UnnamedMetric } from '@fullstory/proto/fs/services/dx-objects';
import { useNotifications } from '@fullstory/react-hooks';
import {
  AppDispatch,
  MetricsRequestActions,
  SegmentRequestActions,
  useUpdateAlertsForMetric,
} from '@fullstory/redux';
import { getOrgId } from '@fullstory/redux/modules/fullstory/selectors';
import { generateRoutePath, METRIC_DETAILS_ROUTE } from '@fullstory/routes';
import { hashObject } from '@fullstory/util/hash';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

/**
 * Returns callbacks to perform mutations on saved metrics
 */
export const useSavedMetricsActions = () => {
  const notifications = useNotifications();
  const dispatch = useDispatch<AppDispatch>();
  const orgId = useSelector(getOrgId);
  const intl = useIntl();
  const updateAlertsForMetric = useUpdateAlertsForMetric();

  const markViewed = useCallback(
    (metricId: string) =>
      dispatch(
        MetricsRequestActions.markMetricViewed({
          orgId,
          metricId,
        }),
      ),
    [dispatch, orgId],
  );

  const setMetric = useCallback(
    async (
      request: Omit<SetMetricRequest, 'orgId'>,
      successMessage: string,
      errorMessage: string,
    ) => {
      try {
        const response = await dispatch(MetricsRequestActions.setMetric({ ...request, orgId }));
        if (successMessage) {
          notifications?.success(successMessage);
        }
        if (request.visibility === Visibility.PUBLIC) {
          dispatch(SegmentRequestActions.getAllSegments(orgId, true));
        }
        return response;
      } catch (e) {
        notifications?.error(errorMessage);
        return null;
      }
    },
    [dispatch, notifications, orgId],
  );

  const create = useCallback(
    async ({
      metric,
      successMessage = intl.formatMessage({
        defaultMessage: 'Successfully created Metric',
        id: 'cxI7Vp',
      }),
      errorMessage = intl.formatMessage({
        defaultMessage: 'Failed to create Metric',
        id: 'K/n4tt',
      }),
    }: {
      metric: Omit<SetMetricRequest, 'orgId'>;
      successMessage?: string;
      errorMessage?: string;
    }) => {
      const payload = {
        ...metric,
        metric: {
          id: '',
          name:
            metric.metric?.name ??
            intl.formatMessage({ defaultMessage: 'Untitled Metric', id: 'V3wYKd' }),
          description: metric.metric?.description ?? '',
          metric: metric.metric?.metric ?? null,
          trend: metric.metric?.trend ?? undefined,
          singleNumber: metric.metric?.singleNumber ?? undefined,
          dimensionality: metric.metric?.dimensionality ?? undefined,
          funnelFilter: null,
        },
        visibility: metric.visibility,
      };
      const response = await setMetric(payload, successMessage, errorMessage);
      return response;
    },
    [intl, setMetric],
  );

  const update = useCallback(
    async (metric: Omit<SetMetricRequest, 'orgId'>) => {
      const response = await setMetric(
        metric,
        intl.formatMessage({ defaultMessage: 'Successfully updated Metric', id: 'AH5A1C' }),
        intl.formatMessage({ defaultMessage: 'Failed to update Metric', id: 'mnkY6S' }),
      );
      updateAlertsForMetric(metric.metric?.id);

      return response;
    },
    [intl, setMetric, updateAlertsForMetric],
  );

  const star = useCallback(
    (metricId: string) =>
      dispatch(MetricsRequestActions.starMetric({ orgId, metricId, starred: true })).catch(() =>
        notifications?.error(
          intl.formatMessage({ defaultMessage: 'Failed to star Metric', id: 'cnPKmh' }),
        ),
      ),
    [dispatch, intl, notifications, orgId],
  );

  const unstar = useCallback(
    (metricId: string) =>
      dispatch(MetricsRequestActions.starMetric({ orgId, metricId, starred: false })).catch(() =>
        notifications?.error(
          intl.formatMessage({ defaultMessage: 'Failed to unstar Metric', id: 'Wg0pCN' }),
        ),
      ),
    [dispatch, intl, notifications, orgId],
  );

  const archive = useCallback(
    async (metricId: string) => {
      try {
        const response = await dispatch(
          MetricsRequestActions.archiveMetric({
            orgId,
            metricId,
            archived: true,
          }),
        );

        updateAlertsForMetric(metricId);

        notifications?.success('Successfully archived Metric.');
        return response;
      } catch (e) {
        notifications?.error('Failed to archive Metric.');
        return null;
      }
    },
    [dispatch, notifications, orgId, updateAlertsForMetric],
  );

  const restore = useCallback(
    (metricId: string) => {
      const unarchivedMetricUrl = generateRoutePath(METRIC_DETAILS_ROUTE, {
        orgId,
        id: metricId,
      });
      return dispatch(MetricsRequestActions.archiveMetric({ orgId, metricId, archived: false }))
        .then(() =>
          notifications?.successLink(
            'Successfully restored Metric. ',
            'See it now.',
            unarchivedMetricUrl,
          ),
        )
        .catch(() => notifications?.error('Failed to restore Metric.'));
    },
    [dispatch, notifications, orgId],
  );

  const createUnnamedMetric = useCallback(
    (metric: UnnamedMetric) => {
      const hash = `${hashObject(metric)}`;
      dispatch(MetricsRequestActions.createUnnamedMetric({ hash, metric, orgId })).catch(() =>
        console.error('Failed to save temporary metric state'),
      );
      return hash;
    },
    [dispatch, orgId],
  );

  return useMemo(
    () => ({
      // We want this to happen transparently to the user, so no notifications are triggered.
      markViewed,
      create,
      update,
      star,
      unstar,
      archive,
      restore,
      createUnnamedMetric,
    }),
    [archive, create, createUnnamedMetric, markViewed, restore, star, unstar, update],
  );
};
